import React from "react";
import { Wrench, ShieldCheck, Siren, MessagesSquare, ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    id: "repairs",
    icon: Wrench,
    overline: "Service 01",
    title: "Repairs",
    text: "When pressure drops, we show up. Diagnostics, parts on the truck, and a same-day fix on most failures — rotary screw, reciprocating, or centrifugal.",
    highlight: "Same-day diagnostics",
    image: "https://images.unsplash.com/photo-1581092335397-9583eb92d232?auto=format&fit=crop&w=1200&q=85",
  },
  {
    id: "planned",
    icon: ShieldCheck,
    overline: "Service 02",
    title: "Planned Maintenance",
    text: "Scheduled servicing tailored to your runtime hours. Oil, filter, valve, and belt programs that prevent unplanned downtime.",
    highlight: "Tailored PM contracts",
    accent: true,
    image: "https://images.unsplash.com/photo-1777118947168-b6e806cb80cf?auto=format&fit=crop&w=1200&q=85",
  },
  {
    id: "emergency",
    icon: Siren,
    overline: "Service 03",
    title: "After-Hours & Emergency Response",
    text: "Catastrophic failure outside regular hours? We offer after-hours emergency response across British Columbia with rapid on-site arrival.",
    highlight: "After-hours, on request",
    accent: true,
    image: "https://images.unsplash.com/photo-1595856898575-9d187bd32fd6?auto=format&fit=crop&w=1200&q=85",
  },
  {
    id: "consult",
    icon: MessagesSquare,
    overline: "Service 04",
    title: "Consultation & Sizing",
    text: "Sizing a new system, energy audits, or specifying replacement equipment? Speak with a technical advisor — no charge.",
    highlight: "Free initial consult",
    image: "https://images.unsplash.com/photo-1581094288338-2314dddb7ece?auto=format&fit=crop&w=1200&q=85",
  },
];

const ServiceCard = ({ service, index }) => {
  const Icon = service.icon;
  const accent = service.accent;
  return (
    <article
      data-testid={`service-card-${service.id}`}
      className={`group relative overflow-hidden border ${
        accent ? "border-blue-900" : "border-slate-200"
      } ${accent ? "bg-slate-950" : "bg-white"} p-8 md:p-10 transition-all hover:-translate-y-1 hover:shadow-[0_12px_40px_-12px_rgba(15,23,42,0.18)] stagger-item`}
      style={{ animationDelay: `${0.05 * index}s` }}
    >
      {/* Background image — visible on right, fading toward the text on left */}
      <div
        className={`absolute inset-0 bg-cover bg-center ${
          accent ? "opacity-[0.55]" : "opacity-[0.55]"
        } group-hover:opacity-[0.85] transition-opacity duration-500`}
        style={{ backgroundImage: `url(${service.image})` }}
        aria-hidden
      />
      {/* Left-to-right gradient wash so text on the left stays crisp while image breathes through */}
      <div
        className={`absolute inset-0 ${
          accent
            ? "bg-gradient-to-r from-slate-950 via-blue-950/85 to-blue-900/20"
            : "bg-gradient-to-r from-white via-white/90 to-white/30"
        }`}
        aria-hidden
      />
      {/* Bottom fade for depth */}
      <div
        className={`absolute inset-0 ${
          accent
            ? "bg-gradient-to-t from-slate-950/70 via-transparent to-transparent"
            : "bg-gradient-to-t from-white/55 via-transparent to-transparent"
        }`}
        aria-hidden
      />

      <div className="relative">
        <div className="flex items-start justify-between">
          <div
            className={`h-12 w-12 flex items-center justify-center ${
              accent ? "bg-blue-500 text-white" : "bg-slate-900 text-white"
            }`}
          >
            <Icon className="h-5 w-5" strokeWidth={2.25} />
          </div>
          <ArrowUpRight
            className={`h-5 w-5 ${
              accent ? "text-blue-300" : "text-slate-300"
            } group-hover:text-blue-400 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all`}
          />
        </div>

        <div
          className={`mt-8 text-[11px] font-bold tracking-[0.2em] uppercase ${
            accent ? "text-blue-300" : "text-blue-600"
          }`}
        >
          {service.overline}
        </div>
        <h3
          className={`font-heading mt-2 text-2xl md:text-3xl font-bold tracking-tight ${
            accent ? "text-white" : "text-slate-900"
          }`}
        >
          {service.title}
        </h3>
        <p className={`mt-4 leading-relaxed ${accent ? "text-slate-200" : "text-slate-700"}`}>
          {service.text}
        </p>

        <div
          className={`mt-8 pt-5 border-t flex items-center justify-between ${
            accent ? "border-blue-900/60" : "border-slate-200/80"
          }`}
        >
          <span
            className={`text-xs font-bold tracking-widest uppercase ${
              accent ? "text-blue-300" : "text-slate-600"
            }`}
          >
            {service.highlight}
          </span>
        </div>
      </div>
    </article>
  );
};

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-slate-50 py-20 md:py-32 px-6 md:px-12 border-y border-slate-200"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
          <div className="md:col-span-5">
            <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-blue-600">
              What We Do
            </div>
            <h2 className="font-heading mt-3 text-4xl md:text-5xl font-black tracking-tighter uppercase text-slate-900">
              Service that keeps the line running.
            </h2>
          </div>
          <p className="md:col-span-6 md:col-start-7 text-lg text-slate-600 leading-relaxed self-end">
            Pneupro is a full-spectrum air compressor service partner. Whether you need a one-off
            repair, a structured maintenance program, or rapid emergency support, our team works to
            minimise your downtime and total cost of ownership.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.id} service={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
